import { Fragment, useEffect, useState } from "react";
import TableBody from "../tables/TableBody";
import { TableDetailsItem, tableMinWidth } from "../tables/tableFunctions";
import Image from "../images/Image";
import { getFirefoxCssFix } from "../commonfunctions/getFirefoxCssFix";
import useTranslation from "../CustomHooks/useTranslation";
import { adminContents } from "../../pages/AdminManagement/components/AdminManagementContent";

interface TableHeaderProps {
  loading: boolean;
  TableDetails: TableDetailsItem;
  handleActions: (id: string, key: string, text?: string) => void;
  handleSort?: (key: string) => void;
  getList?: () => void;
  searchParams?: URLSearchParams;
  listData?: unknown[];
  hasActivateDeactivateMDPrivilege?: boolean;
  hasViewMDPrivilege?: boolean;
  hasEditMDPrivilege?: boolean;
  heightClassName?: string;
  staticRowData?: {
    show: boolean;
    data: Record<string, unknown>;
    position?: 'top' | 'bottom';
  };
  bottomStaticRowData?: {
    show: boolean;
    data: Record<string, unknown>;
    position?: 'top' | 'bottom';
  };
  totalRows: string;
  forceFullWidth?: boolean;
  enableCheckboxes?: boolean;
  selectedIds?: string[];
  onRowSelect?: (id: string, isSelected: boolean) => void;
  allPagesSelected?: boolean;
}

const GridTableHeader = ({
  loading,
  listData,
  TableDetails,
  handleActions,
  searchParams,
  handleSort,
  getList,
  hasActivateDeactivateMDPrivilege,
  hasViewMDPrivilege,
  hasEditMDPrivilege,
  heightClassName,
  staticRowData,
  bottomStaticRowData,
  totalRows,
  forceFullWidth = false,
  enableCheckboxes = false,
  selectedIds = [],
  onRowSelect,
  allPagesSelected = false
}: TableHeaderProps) => {
  const [isSafari, setIsSafari] = useState(false);
  const tableHeaders = Object.entries(TableDetails);
  const currentSort = searchParams?.get("sort");
  const { translate, notEnglish } = useTranslation(adminContents);

  useEffect(() => {
    // Detect Safari browser
    const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    setIsSafari(isSafariBrowser);
  }, []);

  const getSortOrder = (key: string) => {
    if (currentSort) {
      const sortArray = currentSort.split(",");
      const existingSort = sortArray.find((s) => s.startsWith(key));
      if (existingSort) {
        return existingSort.endsWith("asc") ? "asc" : "desc";
      }
    }
    return null;
  };

  // Check if this is Money Account Statement (has "split" column)
  const isMoneyAccountStatement = tableHeaders.some(([, details]) => details.id === 'split');
  
  // Check if this table has few columns (3 or fewer non-action columns) and should use full width
  const nonActionColumns = tableHeaders.filter(([, details]) => details.id !== 'actions');
  const shouldUseFullWidth = isMoneyAccountStatement || nonActionColumns.length <= 3 || forceFullWidth;

  // Function to handle RTL layout for Safari
  const getHeaderOrder = (index: number) => {
    if (!isSafari || !notEnglish) return "";
    return `order-${tableHeaders.length - index}`;
  };

  // Create grid template columns based on tableMinWidth
  const getGridTemplateColumns = () => {
    const columns = [];
    
    // Add checkbox column if enabled
    if (enableCheckboxes) {
      columns.push('60px');
    }
    
    if (shouldUseFullWidth) {
      // For tables with few columns or Money Account Statement, use flex distribution to fill full width
      let flexibleColumns = 0;
      
      // First pass: calculate flexible columns
      const tableColumns = tableHeaders.map(([, details]) => {
        // Use className from table details first, fallback to tableMinWidth function
        const minWidthSource = details.className || tableMinWidth(details.id);
        // Extract the width value from the Tailwind class (e.g., 'min-w-[200px]' -> '200px')
        const widthMatch = minWidthSource.match(/\[(\d+px)\]/);
        const width = widthMatch ? widthMatch[1] : '200px';
        
        // For actions column, use fixed width (no stretching) - always 120px
        if (details.id === 'actions') {
          return '120px';
        }
        
        // For flexible columns, we'll use 1fr but track them
        flexibleColumns++;
        return `minmax(${width}, 1fr)`;
      });
      
      // If we have flexible columns, ensure they fill remaining space with proportional distribution
      if (flexibleColumns > 0) {
        // Recalculate with proportional fr values like TableHeader.tsx
        const finalColumns = tableHeaders.map(([, details]) => {
          const minWidthSource = details.className || tableMinWidth(details.id);
          const widthMatch = minWidthSource.match(/\[(\d+px)\]/);
          const width = widthMatch ? widthMatch[1] : '170px';

          if (details.id === 'actions') {
            return '120px';
          }

          if (details.noStretch) {
            return `minmax(${width}, max-content)`;
          }

          // Use proportional fr values based on minimum width like TableHeader.tsx
          const minWidthClass = minWidthSource;
          if (minWidthClass.includes("110px")) return "minmax(110px, 0.9fr)"; // Company ID - smaller
          if (minWidthClass.includes("120px")) return "minmax(120px, 1fr)";   // User/USSD code - medium
          if (minWidthClass.includes("220px")) return "minmax(220px, 1.6fr)"; // Company Name - medium-large
          if (minWidthClass.includes("250px")) return "minmax(250px, 1.8fr)"; // Parent User Code and Name - large
          if (minWidthClass.includes("248px")) return "minmax(248px, 1.8fr)"; // Transaction Date - large
          if (minWidthClass.includes("200px")) return "minmax(200px, 1.4fr)"; // Date fields - medium-large
          if (minWidthClass.includes("160px")) return "minmax(160px, 1.2fr)"; // Transaction ID - medium
          if (minWidthClass.includes("150px")) return "minmax(150px, 1.2fr)"; // Phone number - medium
          if (minWidthClass.includes("140px")) return "minmax(140px, 1fr)"; // Transaction category - medium
          if (minWidthClass.includes("280px")) return "minmax(280px, 2.2fr)"; // Email - larger
          if (minWidthClass.includes("130px")) return "minmax(130px, 1fr)"; // Status - medium
          if (minWidthClass.includes("240px")) return "minmax(240px, 1.7fr)"; // Transaction Date - large
          if (minWidthClass.includes("450px")) return "minmax(450px, 3.5fr)"; // On Target Service Fee Percentage - extra large
          if (minWidthClass.includes("400px")) return "minmax(400px, 3fr)"; // User Name (User Code) - very large
          if (minWidthClass.includes("320px")) return "minmax(320px, 2.5fr)"; // Reference - extra large
          if (minWidthClass.includes("203px")) return "minmax(203px, 1.5fr)"; // Contract dates - medium-large
          if (minWidthClass.includes("200px") && details.id?.includes("balance")) return "minmax(200px, 1.5fr)"; // Balance fields - medium-large
          return `minmax(${width}, 1.5fr)`; // Default - medium-large
        });
        columns.push(...finalColumns);
      } else {
        columns.push(...tableColumns);
      }
    } else {
      // For all other reports with many columns, use the original logic with max-content for scrolling
      const tableColumns = tableHeaders.map(([, details]) => {
        const minWidth = tableMinWidth(details.id);
        const widthMatch = minWidth.match(/\[(\d+px)\]/);
        const width = widthMatch ? widthMatch[1] : '200px';
        return `minmax(${width}, max-content)`;
      });
      columns.push(...tableColumns);
    }
    
    return columns.join(' ');
  };

  const hasOnlyOneRecord = listData && listData.length === 1;
  const isFundTransferLogs = typeof window !== 'undefined' && window.location.pathname.includes('fund-transfer-logs');

  return (
    <div className={`${heightClassName || 'h-tableHeight'} block w-full ${getFirefoxCssFix('scroll')} `}>
      <div className={`${loading ? 'bg-white' : ''} ${hasOnlyOneRecord && isFundTransferLogs ? '' : 'border border-[#D8DFE9]'} overflow-x-auto relative z-0 max-h-full scrollBar w-full ${hasOnlyOneRecord && isFundTransferLogs ? 'overflow-visible pb-20' : 'overflow-y-auto'}`}>
        <div 
          className={`max-h-full text-left grid ${shouldUseFullWidth ? 'sm:w-full w-max' : 'w-max'}`}
          style={{ 
            gridTemplateColumns: getGridTemplateColumns(),
            minWidth: 'max-content',
          }}
        >
          {/* Header */}
          <div 
            className="contents" 
            dir={notEnglish && !isSafari ? "rtl" : "ltr"}
          >
            {enableCheckboxes && (
              <div className="sticky top-[0px] z-[10] bg-p_light border-t border-l border-r border-[#D8DFE9] text-nowrap h-[45px] text-center text-large w-full min-w-[60px]">
                <span className="flex items-center justify-center h-full">
                  <p className="px-3 text-sm font-medium text-gray-700"></p>
                </span>
              </div>
            )}
            {tableHeaders.map(([header, details], indexTable = 0) => {
              const isLastColumn = indexTable === tableHeaders.length - 1;
              const isFixedColumn = details.fixed === true && isLastColumn;
              return (
              <Fragment key={indexTable + "indexTable"}>
                {details.id !== "actions" ? (
                  <div
                    data-testid={`header-${header.toLowerCase().replace(/\s+/g, '_')}`}
                    className={`sticky top-[0px] ${isFixedColumn ? 'right-0 z-[50]' : 'z-[10]'} bg-p_light border-t border-l ${tableHeaders[tableHeaders.length - 1]?.[1]?.id === 'actions' && indexTable === tableHeaders.length - 2 ? '' : 'border-r'} border-[#D8DFE9] text-nowrap h-[45px] text-left text-large w-full ${details.className || ""} ${getHeaderOrder(indexTable)} ${notEnglish && isSafari ? "flex-1" : ""}`}
                    dir={notEnglish && isSafari ? "rtl" : ""}
                    style={isFixedColumn ? { right: '0px' } : {}} data-testid={`header-${details.id}`}
                  >
                    <span className="flex items-center relative h-full">
                      {loading ? (
                        <div className="h-[24px] w-full bg-gray-200 rounded animate-pulse mx-2.5"></div>
                      ) : 
                      <>
                        <p className="px-1.5">{translate(header)}</p>
                        {details.sort && (
                          <div className={`ml-2 ${getSortOrder(details.sortKey || details.id) === "desc" ? "rotate-180" : ""}`}>
                            <Image
                              src={getSortOrder(details.sortKey || details.id) ? "sort" : "inactive_sort"}
                              testId={`sort_${details.id}`}
                              onClick={() => handleSort ? handleSort(details.sortKey || details.id) : {}}
                              className={`h-5 w-5 ${!loading ? "cursor-pointer" : ""}`}
                            />
                          </div>
                        )}
                      </>}
                    </span>
                  </div>
                ) : (
                  <div
                    data-testid={`header-${header.toLowerCase().replace(/\s+/g, '_')}`}
                    className={`${loading ? "" : "sticky"} top-[0px] right-0 z-[50] bg-p_light border-t border-r border-[#D8DFE9] p-0 text-center text-large w-full ${notEnglish && isSafari ? "flex-none" : ""}`}
                    dir={notEnglish && isSafari ? "rtl" : ""}
                    style={{ right: '0px' }}
                    data-testid={`header-${details.id}`}
                  >
                    <span
                      className={`border-l border-[#D8DFE9] text-center bg-p_light ${loading ? 'px-2' : 'px-3'} py-[10px] flex justify-center`}
                    >
                      {loading ? (
                        <div className="h-6 w-full bg-gray-200 rounded animate-pulse"></div>
                      ) : details?.customHeader ? (
                        translate(details.customHeader)
                      ) : details?.actionTypes.length > 1 ? (
                        translate("Actions")
                      ) : (
                        translate("Action")
                      )}
                    </span>
                  </div>
                )}
              </Fragment>
            );
            })}
          </div>

          {/* Body */}
          {/* Top Static Row (Closing Balance) */}
          {staticRowData?.show && (
            <div className="contents">
              {tableHeaders.map(([, details], idx) => {
                const lastIdx = tableHeaders.length - 1;

                if (idx === lastIdx) {
                  return (
                    <div
                      key={idx}
                      className={`${details.className || ''} sticky right-[1px] bg-white text-center p-2 z-20`}
                      style={{ minWidth: 'fit-content' }}
                    >
                      {loading ? (
                        <div className="h-4 w-16"></div>
                      ) : (
                        staticRowData.data?.lastColumnValue ?? 'AFA 0.00'
                      )}
                    </div>
                  );
                } else if (idx === 0) {
                  return (
                    <div
                      key={idx}
                      className="p-2 whitespace-nowrap text-left bg-white text-body2 ml-1"
                      style={{ gridColumn: `span ${lastIdx}` }}
                    >
                      {loading ? (
                        <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
                      ) : (
                        staticRowData.data?.noticeMessage ?? "As on 20-01-2025 Closing balance is AFA 200"
                      )}
                    </div>
                  );
                  } else {
                    return <Fragment key={idx}></Fragment>;
                  }
              })}
            </div>
          )}

          {loading ? (
            // Skeleton loading rows
            Array.from({ length: 10 }).map((_, index) => (
              <div key={index} className="contents">
                {enableCheckboxes && (
                  <div className="animation-plus text-body2 bg-white border border-[#D8DFE9] p-1.5">
                    <div className="h-[30px] bg-gray-200 rounded"></div>
                  </div>
                )}
                {tableHeaders.map(([key, details], colIndex) => {
                  const isLastColumn = colIndex === tableHeaders.length - 1;
                  const isFixedColumn = details.fixed === true && isLastColumn;
                  return (
                    <Fragment key={`${key}-${index}`}>
                      <div className={`animation-plus text-body2 bg-white border border-[#D8DFE9] ${details.id === 'actions' ? 'px-2 py-2' : 'p-2.5'} ${tableMinWidth(details.id)} ${details.className || ""} ${isFixedColumn ? 'sticky right-0 z-[11]' : ''}`}>
                        <div className="h-[30px] bg-gray-200 rounded"></div>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            ))
          ) : (listData && listData.length > 0 ? (
            <>
              {listData.map((item, index) => (
                <div key={index} className="contents">
                  <TableBody
                    tableBodyItem={item as Record<string, unknown>}
                    TableDetails={TableDetails}
                    tableBodyIndex={index}
                    handleActions={handleActions}
                    getList={getList}
                    hasActivateDeactivateMDPrivilege={hasActivateDeactivateMDPrivilege}
                    hasViewMDPrivilege={hasViewMDPrivilege}
                    hasEditMDPrivilege={hasEditMDPrivilege}
                    heightClassName={heightClassName}
                    totalRows={totalRows}
                    enableCheckboxes={enableCheckboxes}
                    selectedIds={selectedIds}
                    onRowSelect={onRowSelect}
                    allPagesSelected={allPagesSelected}
                  />
                </div>
              ))}
            </>
          ) : (
            <div className="contents">
              <div className="py-4 text-center col-span-full">
                {translate("No data available")}
              </div>
            </div>
          ))}

          {/* Bottom Static Row */}
          {bottomStaticRowData?.show && (
            <div className="contents">
              {bottomStaticRowData.data?.firstColumnSpan ? (
                <>
                  {/* First column with colspan */}
                  <div
                    className={`p-2 whitespace-nowrap text-left border-r ${bottomStaticRowData.data?.textColor || 'text-p_primary'} ${bottomStaticRowData.data?.fontWeight || 'font-semibold'}`}
                    style={{ gridColumn: `span ${bottomStaticRowData.data.firstColumnSpan as number}` }}
                  >
                    {loading ? (
                      <div className="h-4 w-16 rounded animate-pulse"></div>
                    ) : (
                      bottomStaticRowData.data?.firstColumnText ?? "COGS%"
                    )}
                  </div>
                  {/* Remaining columns */}
                  {(bottomStaticRowData.data?.remainingColumns as string[])?.map((value: string, colIdx: number) => {
                    const isLastColumn = colIdx === (bottomStaticRowData.data.remainingColumns as string[]).length - 1;
                    const isActionsColumn = tableHeaders[(bottomStaticRowData.data.firstColumnSpan as number) + colIdx + 1]?.[1]?.id === "actions";
                    
                    return (
                      <div
                        key={colIdx}
                        className={`p-2 whitespace-nowrap text-start ${bottomStaticRowData.data?.textColor || 'text-p_primary'} ${bottomStaticRowData.data?.fontWeight || 'font-semibold'} ${
                          isActionsColumn 
                            ? 'sticky right-[1px] bg-white z-20' 
                            : isLastColumn 
                              ? '' 
                              : 'border-r'
                        }`}
                      >
                        {loading ? (
                          <div className="h-4 w-16 rounded animate-pulse"></div>
                        ) : (
                          value
                        )}
                      </div>
                    );
                  })}
                </>
              ) : (
                /* Default structure for other reports */
                tableHeaders.map(([, details], idx) => {
                  const lastIdx = tableHeaders.length - 1;

                  if (idx === lastIdx) {
                    return (
                      <div
                        key={idx}
                        className={`${details.className || ''} sticky right-[1px] bg-white text-center p-2 z-20`}
                        style={{ minWidth: 'fit-content' }}
                      >
                        {loading ? (
                          <div className="h-4 w-16"></div>
                        ) : (
                          bottomStaticRowData.data?.lastColumnValue ?? 'AFA 0.00'
                        )}
                      </div>
                    );
                  } else if (idx === 0) {
                    return (
                      <div
                        key={idx}
                        className="p-2 whitespace-nowrap text-left ml-1 text-body2"
                        style={{ gridColumn: `span ${lastIdx}` }}
                      >
                        {loading ? (
                          <div className="h-4 w-64 rounded animate-pulse"></div>
                        ) : (
                          bottomStaticRowData.data?.noticeMessage ?? "As on 20-01-2025 Opening balance is AFA 200"
                        )}
                      </div>
                    );
                  } else {
                    return <Fragment key={idx}></Fragment>;
                  }
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GridTableHeader;